import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SiteHeader.module.css';

const API = process.env.REACT_APP_API_URL || '/api';

const CATEGORIES = [
  { label: 'Técnico Superior Universitario', type: 'tsu' },
  { label: 'Licenciaturas',                  type: 'degree' },
  { label: 'Maestrías',                      type: 'master' },
  { label: 'Especializaciones',              type: 'speciality' },
];

const OTHER_NAV = [
  { label: 'Admisiones y becas', path: '/becas' },
  { label: 'Sobre nosotros',     path: '/nosotros' },
  { label: 'Contacto',           path: '/#contacto' },
];

function SiteHeader() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [ofertaOpen, setOfertaOpen] = useState(false);
  const [mobileOfertaOpen, setMobileOfertaOpen] = useState(false);
  const [lists, setLists] = useState({ tsu: [], degree: [], master: [], speciality: [] });
  const ofertaRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/tsu/list`).then((r) => r.json()),
      fetch(`${API}/degrees/list`).then((r) => r.json()),
      fetch(`${API}/master-degrees/list`).then((r) => r.json()),
      fetch(`${API}/specialities/list`).then((r) => r.json()),
    ]).then(([tsu, deg, mas, sp]) => {
      setLists({
        tsu:        tsu.data || [],
        degree:     deg.data || [],
        master:     mas.data || [],
        speciality: sp.data  || [],
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ofertaRef.current && !ofertaRef.current.contains(e.target)) {
        setOfertaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = (path) => {
    setMenuOpen(false);
    setOfertaOpen(false);
    if (path) navigate(path);
  };

  const handleCourseNav = (type, id) => {
    setMenuOpen(false);
    setOfertaOpen(false);
    setMobileOfertaOpen(false);
    navigate(`/oferta?type=${type}&id=${id}`);
  };

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <div className={styles.logosGroup}>
          <button
            type="button"
            className={styles.homeLogoBtn}
            onClick={() => navigate('/')}
            aria-label="Ir al inicio"
          >
            <img src="/logos/logos_1.png" alt="ODM" className={styles.homeLogo} />
          </button>
          <img src="/RVOE.png" alt="RVOE" className={styles.rvoeLogo} />
        </div>

        {/* Nav desktop */}
        <nav className={styles.desktopNav} aria-label="Navegación principal">
          <ul className={styles.menu}>

            {/* Oferta educativa — dropdown */}
            <li className={styles.menuItemWrapper} ref={ofertaRef}>
              <button
                type="button"
                className={`${styles.menuItem} ${ofertaOpen ? styles.menuItemActive : ''}`}
                aria-haspopup="true"
                aria-expanded={ofertaOpen}
                onClick={() => setOfertaOpen((o) => !o)}
              >
                Oferta educativa
                <span className={`${styles.chevron} ${ofertaOpen ? styles.chevronUp : ''}`} aria-hidden="true" />
              </button>

              <div className={`${styles.ofertaDropdown} ${ofertaOpen ? styles.ofertaDropdownOpen : ''}`}>
                {/* Preparatoria — enlace directo */}
                <div className={styles.dropdownCol}>
                  <p className={styles.dropdownCatTitle}>Preparatoria</p>
                  <ul className={styles.dropdownList}>
                    <li>
                      <button
                        type="button"
                        className={styles.dropdownItem}
                        onClick={() => handleNav('/oferta?type=bachillerato')}
                      >
                        Bachillerato ODM
                      </button>
                    </li>
                  </ul>
                </div>

                {CATEGORIES.map((cat) => (
                  <div key={cat.type} className={styles.dropdownCol}>
                    <p className={styles.dropdownCatTitle}>{cat.label}</p>
                    <ul className={styles.dropdownList}>
                      {lists[cat.type].length === 0 ? (
                        <li className={styles.dropdownEmpty}>Sin programas</li>
                      ) : (
                        lists[cat.type].map((item) => (
                          <li key={item._id}>
                            <button
                              type="button"
                              className={styles.dropdownItem}
                              onClick={() => handleCourseNav(cat.type, item._id)}
                            >
                              {item.name}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </li>

            {OTHER_NAV.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => handleNav(item.path)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <button
          type="button"
          className={styles.loginBtn}
          onClick={() => navigate('/portal')}
        >
          Iniciar sesión
        </button>

        {/* Hamburger */}
        <button
          type="button"
          className={styles.hamburger}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuOpen}
        >
          <span className={`${styles.bar} ${menuOpen ? styles.bar1Open : ''}`} />
          <span className={`${styles.bar} ${menuOpen ? styles.bar2Open : ''}`} />
          <span className={`${styles.bar} ${menuOpen ? styles.bar3Open : ''}`} />
        </button>
      </div>

      {/* Menú mobile */}
      <div
        className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav aria-label="Menú móvil">
          <ul className={styles.mobileList}>

            {/* Oferta educativa expandable */}
            <li>
              <button
                type="button"
                className={styles.mobileItem}
                onClick={() => setMobileOfertaOpen((o) => !o)}
                tabIndex={menuOpen ? 0 : -1}
              >
                Oferta educativa
                <span className={`${styles.chevron} ${mobileOfertaOpen ? styles.chevronUp : ''}`} aria-hidden="true" />
              </button>

              <div className={`${styles.mobileOfertaTree} ${mobileOfertaOpen ? styles.mobileOfertaTreeOpen : ''}`}>
                {/* Preparatoria — enlace directo */}
                <div>
                  <p className={styles.mobileCatTitle}>Preparatoria</p>
                  <button
                    type="button"
                    className={styles.mobileCourseItem}
                    onClick={() => handleNav('/oferta?type=bachillerato')}
                    tabIndex={menuOpen && mobileOfertaOpen ? 0 : -1}
                  >
                    Bachillerato ODM
                  </button>
                </div>

                {CATEGORIES.map((cat) => (
                  <div key={cat.type}>
                    <p className={styles.mobileCatTitle}>{cat.label}</p>
                    {lists[cat.type].map((item) => (
                      <button
                        key={item._id}
                        type="button"
                        className={styles.mobileCourseItem}
                        onClick={() => handleCourseNav(cat.type, item._id)}
                        tabIndex={menuOpen && mobileOfertaOpen ? 0 : -1}
                      >
                        {item.name}
                      </button>
                    ))}
                    {lists[cat.type].length === 0 && (
                      <p className={styles.mobileEmpty}>Sin programas</p>
                    )}
                  </div>
                ))}
              </div>
            </li>

            {OTHER_NAV.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  className={styles.mobileItem}
                  onClick={() => handleNav(item.path)}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <button
          type="button"
          className={styles.mobileLoginBtn}
          onClick={() => handleNav('/portal')}
          tabIndex={menuOpen ? 0 : -1}
        >
          Iniciar sesión
        </button>
      </div>
    </header>
  );
}

export default SiteHeader;
